import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useFilteredTasks } from '@/hooks/useFilteredTasks';
import { useAppUsers } from '@/hooks/useAppUsers';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Clock, User, AlertCircle, Check, X, Filter, Loader2, CalendarIcon } from 'lucide-react';
import { SearchBar } from '@/components/common/SearchBar';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { EscalationTaskCompletionModal } from '@/components/tasks/EscalationTaskCompletionModal';
import { TaskDetailSheet } from '@/components/tasks/TaskDetailSheet';
import { useEscalationContacts } from '@/hooks/useEscalationContacts';
import { useEscalationTasksBySearch } from '@/hooks/useEscalationTasksBySearch';
import { useTasksByClient } from '@/hooks/useTasksByClient';
import { ContactPicker } from '@/components/common/ContactPicker';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { addDays, format } from 'date-fns';
import { parseDateSafe } from '@/lib/timezoneUtils';

const statusConfig = {
  pending: {
    bgColor: "bg-slate-50 dark:bg-slate-800/50",
    borderColor: "border-slate-100 dark:border-slate-700",
    countBg: "bg-slate-100 dark:bg-slate-700",
    countText: "text-slate-700 dark:text-slate-300"
  },
  in_progress: {
    bgColor: "bg-blue-50 dark:bg-blue-900/30",
    borderColor: "border-blue-100 dark:border-blue-800",
    countBg: "bg-blue-100 dark:bg-blue-900",
    countText: "text-blue-700 dark:text-blue-300"
  },
  completed: {
    bgColor: "bg-emerald-50 dark:bg-emerald-900/30",
    borderColor: "border-emerald-100 dark:border-emerald-800",
    countBg: "bg-emerald-100 dark:bg-emerald-900",
    countText: "text-emerald-700 dark:text-emerald-300"
  },
  blocked: {
    bgColor: "bg-rose-50 dark:bg-rose-900/30",
    borderColor: "border-rose-100 dark:border-rose-800",
    countBg: "bg-rose-100 dark:bg-rose-900",
    countText: "text-rose-700 dark:text-rose-300"
  }
};

// Debounce hook for search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
}

export default function Tasks() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { users } = useAppUsers();
  const { data: currentUser } = useCurrentAppUser();
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [assignedFilter, setAssignedFilter] = useState(null);
  
  // Set default filter to current user's tasks
  React.useEffect(() => {
    if (currentUser?.id && assignedFilter === null) {
      setAssignedFilter(currentUser.id);
    }
  }, [currentUser?.id, assignedFilter]);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [contactFilter, setContactFilter] = useState(null);

  
  // Debounced search for RPC
  const debouncedSearch = useDebounce(searchQuery, 300);
  
  // Build filters for RPC
  const rpcFilters = useMemo(() => ({
    assignedTo: assignedFilter === 'all' || assignedFilter === null ? null : assignedFilter,
    priority: priorityFilter === 'all' ? null : priorityFilter,
    taskType: typeFilter === 'all' ? null : typeFilter,
    search: debouncedSearch || null,
  }), [assignedFilter, priorityFilter, typeFilter, debouncedSearch]);
  
  const { tasks: rpcTasks, counts, isLoading, updateTask, deleteTask, createTask } = useFilteredTasks(rpcFilters);

  // Busca por nome de cliente (escalações + contato direto + via deal)
  const tasksFromClientSearch = useEscalationTasksBySearch(debouncedSearch);

  // Filtro Cliente ativo: traz todas as tasks daquele cliente
  const tasksFromContactFilter = useTasksByClient({ contactId: contactFilter?.id ?? null });

  const tasks = useMemo(() => {
    const base = rpcTasks ?? [];
    const ids = new Set(base.map(t => t.id));
    const merged = [...base];
    for (const t of tasksFromClientSearch ?? []) {
      if (!ids.has(t.id)) { ids.add(t.id); merged.push(t); }
    }
    for (const t of tasksFromContactFilter ?? []) {
      if (!ids.has(t.id)) { ids.add(t.id); merged.push(t); }
    }
    return merged;
  }, [rpcTasks, tasksFromClientSearch, tasksFromContactFilter]);


  // Mapa session_id -> contato, para tasks de Escalação CS
  const escalationSessionIds = useMemo(
    () => (tasks || [])
      .filter(t => t?.metadata?.source === 'cs_escalation' && t?.metadata?.session_id)
      .map(t => t.metadata.session_id),
    [tasks]
  );
  const escalationContacts = useEscalationContacts(escalationSessionIds);
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { register, handleSubmit, reset, setValue } = useForm();
  
  // Due date quick select state
  const [dueDate, setDueDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Date helper functions
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (date) => {
    if (!date) return false;
    const tomorrow = addDays(new Date(), 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  const handleQuickDate = (type) => {
    const date = type === 'today' 
      ? new Date() 
      : addDays(new Date(), 1);
    setDueDate(date);
    setValue('due_date', format(date, 'yyyy-MM-dd'));
  };

  const handleCustomDate = (date) => {
    if (date) {
      setDueDate(date);
      setValue('due_date', format(date, 'yyyy-MM-dd'));
    }
    setCalendarOpen(false);
  };
  
  // Detail sheet state
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Escalation task completion modal state
  const [escalationTask, setEscalationTask] = useState(null);
  const [showEscalationModal, setShowEscalationModal] = useState(false);

  // Deep-link: always fetch the task directly by ID (doesn't depend on the filtered list loading)
  const deepLinkTaskId = searchParams.get('taskId');
  const handledDeepLinkRef = useRef(null);
  const { data: deepLinkTask, isFetched: deepLinkFetched, isError: deepLinkError } = useQuery({
    queryKey: ['task-by-id', deepLinkTaskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('*, metadata')
        .eq('id', deepLinkTaskId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!deepLinkTaskId,
  });

  useEffect(() => {
    if (!deepLinkTaskId || handledDeepLinkRef.current === deepLinkTaskId) return;
    const clear = () => {
      handledDeepLinkRef.current = deepLinkTaskId;
      const next = new URLSearchParams(searchParams);
      next.delete('taskId');
      setSearchParams(next, { replace: true });
    };
    const found = tasks?.find(t => t.id === deepLinkTaskId) || deepLinkTask;
    if (found) {
      setSelectedTask(found);
      setIsDetailOpen(true);
      clear();
    } else if (deepLinkFetched || deepLinkError) {
      toast.error('Tarefa não encontrada');
      clear();
    }
  }, [deepLinkTaskId, tasks, deepLinkTask, deepLinkFetched, deepLinkError]);

  useEffect(() => {
    if (!deepLinkTaskId) handledDeepLinkRef.current = null;
  }, [deepLinkTaskId]);

  const columns = [
    { id: 'pending', labelKey: 'tasks.columns.pending', color: 'bg-muted/50 dark:bg-slate-800' },
    { id: 'in_progress', labelKey: 'tasks.columns.inProgress', color: 'bg-blue-100/50 dark:bg-blue-900/30' },
    { id: 'completed', labelKey: 'tasks.columns.completed', color: 'bg-emerald-100/50 dark:bg-emerald-900/30' },
    { id: 'blocked', labelKey: 'tasks.columns.blocked', color: 'bg-red-100/50 dark:bg-red-900/30' },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getPriorityLabel = (priority) => {
    const priorityMap = {
      low: t('tasks.priorities.low'),
      medium: t('tasks.priorities.medium'),
      high: t('tasks.priorities.high'),
      urgent: t('tasks.priorities.urgent')
    };
    return priorityMap[priority] || priority;
  };

  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData('taskId', taskId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    const task = tasks?.find(t => t.id === taskId);
    
    // If it's an escalation task being completed, open special modal
    if (newStatus === 'completed' && task?.metadata?.source === 'cs_escalation') {
      setEscalationTask(task);
      setShowEscalationModal(true);
      return;
    }
    
    try {
      await updateTask.mutateAsync({
        id: taskId,
        status: newStatus,
        ...(newStatus === 'completed' && { completed_at: new Date().toISOString() })
      });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  // Handler for escalation task completion
  const handleEscalationComplete = async (taskId, response) => {
    await updateTask.mutateAsync({
      id: taskId,
      notes: response,
      status: 'completed',
      completed_at: new Date().toISOString()
    });
    setShowEscalationModal(false);
    setEscalationTask(null);
  };

  // Handler for Kommo review task actions
  const handleKommoReviewAction = async (task, action) => {
    try {
      if (action === 'belongs') {
        // Just mark as completed
        await updateTask.mutateAsync({
          id: task.id,
          status: 'completed',
          completed_at: new Date().toISOString()
        });
        toast.success('Verificação concluída - case pertence ao deal');
      } else {
        // Create new deal and re-link item
        const metadata = task.metadata;
        
        // 1. Get contact from original deal
        const { data: originalDeal, error: dealError } = await supabase
          .from('deals')
          .select('contact_id, name')
          .eq('id', metadata.original_deal_id)
          .single();
        
        if (dealError) throw dealError;
        
        // 2. Create new WON Deal
        const { data: newDeal, error: newDealError } = await supabase
          .from('deals')
          .insert({
            contact_id: originalDeal.contact_id,
            source: 'direct',
            stage: 'WON',
            name: `Import Kommo - Separado de ${originalDeal.name}`,
            notes: 'Deal criado após verificação de importação Kommo - case não pertencia ao deal original'
          })
          .select('id')
          .single();
        
        if (newDealError) throw newDealError;
        
        // 3. Update item to new deal
        const { error: updateError } = await supabase
          .from('items')
          .update({ deal_id: newDeal.id })
          .eq('id', metadata.item_id);
        
        if (updateError) throw updateError;
        
        // 4. Mark task as completed
        await updateTask.mutateAsync({
          id: task.id,
          status: 'completed',
          completed_at: new Date().toISOString()
        });
        
        toast.success('Novo deal criado e case revinculado');
      }
    } catch (error) {
      console.error('Error handling Kommo review:', error);
      toast.error('Erro: ' + error.message);
    }
  };

  const onSubmit = async (data) => {
    try {
      // Limpar campos vazios para evitar erro de tipo
      const cleanedData = {
        ...data,
        estimated_hours: data.estimated_hours ? parseInt(data.estimated_hours, 10) : null,
        status: 'pending',
        created_by: currentUser?.id || null,
      };
      
      await createTask.mutateAsync(cleanedData);
      setIsCreateOpen(false);
      reset();
      setDueDate(null);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };
  
  // Reset due date when dialog closes
  const handleDialogChange = (open) => {
    setIsCreateOpen(open);
    if (!open) {
      setDueDate(null);
      setCalendarOpen(false);
    }
  };

  // Apply Cliente filter (client-side)
  const displayedTasks = useMemo(() => {
    if (!contactFilter?.id) return tasks ?? [];
    const cid = contactFilter.id;
    const escMatchIds = new Set(
      Object.entries(escalationContacts || {})
        .filter(([, c]) => c?.contactId === cid)
        .map(([sid]) => sid)
    );
    return (tasks ?? []).filter((t) => {
      if (t.contact_id === cid) return true;
      if (t.deals?.contact_id === cid) return true;
      const sid = t?.metadata?.session_id;
      if (sid && escMatchIds.has(sid)) return true;
      return false;
    });
  }, [tasks, contactFilter, escalationContacts]);

  const getTasksByStatus = useCallback((status) => {
    return displayedTasks?.filter(task => task.status === status) || [];
  }, [displayedTasks]);

  // Open task detail
  const handleOpenDetail = (task) => {
    setSelectedTask(task);
    setIsDetailOpen(true);
  };

  // Handle task update from sheet
  const handleUpdateTask = async (id, data) => {
    await updateTask.mutateAsync({ id, ...data });
  };

  // Handle task delete from sheet
  const handleDeleteTask = async (id) => {
    await deleteTask.mutateAsync(id);
    setIsDetailOpen(false);
    setSelectedTask(null);
  };

  // Handle complete from sheet (for escalations)
  const handleCompleteFromSheet = (task) => {
    if (task.metadata?.source === 'cs_escalation') {
      setEscalationTask(task);
      setShowEscalationModal(true);
      setIsDetailOpen(false);
    } else {
      handleUpdateTask(task.id, {
        status: 'completed',
        completed_at: new Date().toISOString()
      });
    }
  };

  // Check if any filter is active (exclude currentUser as default)
  const hasActiveFilters = (assignedFilter !== 'all' && assignedFilter !== currentUser?.id) || priorityFilter !== 'all' || typeFilter !== 'all' || searchQuery || !!contactFilter;

  // Clear all filters - reset to "My Tasks" as default
  const clearFilters = () => {
    setSearchQuery('');
    setAssignedFilter(currentUser?.id || 'all');
    setPriorityFilter('all');
    setTypeFilter('all');
    setContactFilter(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 p-4 md:p-6 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            {/* Search Bar */}
            <div className="flex-1">
              <SearchBar
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por título, descrição ou cliente..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 items-center">
              {/* Add Button */}
              <Dialog open={isCreateOpen} onOpenChange={handleDialogChange}>
                <DialogTrigger asChild>
                  <Button
                    className="rounded-full h-9 w-9 p-0 flex-shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{t('tasks.createNewTask')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                      <Label>{t('tasks.titleRequired')}</Label>
                      <Input {...register('title', { required: true })} placeholder={t('tasks.titlePlaceholder')} />
                    </div>

                    <div>
                      <Label>{t('tasks.description')}</Label>
                      <Textarea {...register('description')} placeholder={t('tasks.descriptionPlaceholder')} rows={4} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{t('tasks.priority')}</Label>
                        <Select onValueChange={(value) => setValue('priority', value)} defaultValue="medium">
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">{t('tasks.priorities.low')}</SelectItem>
                            <SelectItem value="medium">{t('tasks.priorities.medium')}</SelectItem>
                            <SelectItem value="high">{t('tasks.priorities.high')}</SelectItem>
                            <SelectItem value="urgent">{t('tasks.priorities.urgent')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>{t('tasks.assignTo')}</Label>
                        <Select onValueChange={(value) => setValue('assigned_to', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('tasks.selectUser')} />
                          </SelectTrigger>
                          <SelectContent>
                            {[...users]
                              .sort((a, b) => {
                                const nameA = (a.display_name || a.email || '').toLowerCase();
                                const nameB = (b.display_name || b.email || '').toLowerCase();
                                return nameA.localeCompare(nameB);
                              })
                              .map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  {user.display_name || user.email}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{t('tasks.dueDate')}</Label>
                        <div className="flex gap-2 mt-1.5 flex-wrap">
                          <Button
                            type="button"
                            variant={isToday(dueDate) ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleQuickDate('today')}
                          >
                            {t('tasks.dueDateToday')}
                          </Button>
                          
                          <Button
                            type="button"
                            variant={isTomorrow(dueDate) ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleQuickDate('tomorrow')}
                          >
                            {t('tasks.dueDateTomorrow')}
                          </Button>
                          
                          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant={dueDate && !isToday(dueDate) && !isTomorrow(dueDate) ? "default" : "outline"}
                                size="sm"
                                className="gap-2"
                              >
                                <CalendarIcon className="h-4 w-4" />
                                {dueDate && !isToday(dueDate) && !isTomorrow(dueDate)
                                  ? format(dueDate, "dd/MM/yyyy")
                                  : t('tasks.dueDateCustom')
                                }
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={dueDate}
                                onSelect={handleCustomDate}
                                initialFocus
                                className="pointer-events-auto"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <div>
                        <Label>{t('tasks.estimatedDuration')}</Label>
                        <Input type="number" {...register('estimated_hours')} placeholder="8" />
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                        {t('tasks.cancel')}
                      </Button>
                      <Button type="submit" disabled={createTask.isPending}>
                        {createTask.isPending ? t('tasks.creating') : t('tasks.createTask')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Filter Toggle Button */}
              <Button
                variant="ghost"
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  "rounded-full h-9 w-9 p-0 flex-shrink-0",
                  showFilters
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "hover:bg-muted"
                )}
              >
                <Filter className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mb-4 p-4 bg-card rounded-lg border shadow-sm">
              <div className="flex flex-wrap gap-3">
                {/* Assigned To Filter */}
                <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                  <SelectTrigger className="w-[180px] h-9 rounded-full">
                    <User className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Responsável" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {currentUser && (
                      <SelectItem value={currentUser.id}>Minhas Tarefas</SelectItem>
                    )}
                    {users
                      .filter(u => u.id !== currentUser?.id)
                      .filter(u => u.active)
                       .filter(u => {
                        const name = u.display_name || u.email || '';
                        return !name.startsWith('placeholder_') && 
                               name !== 'Unknown' && 
                               name !== 'System Auto';
                      })
                      .sort((a, b) => {
                        const getUserName = (u) => u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '';
                        const nameA = getUserName(a).toLowerCase();
                        const nameB = getUserName(b).toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((user) => {
                        const userName = user.display_name || [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
                        return (
                          <SelectItem key={user.id} value={user.id}>
                            {userName}
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>

                {/* Priority Filter */}
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-[150px] h-9 rounded-full">
                    <AlertCircle className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Prioridade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="low">Baixa</SelectItem>
                  </SelectContent>
                </Select>

                {/* Type Filter */}
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[180px] h-9 rounded-full">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Tipos</SelectItem>
                    <SelectItem value="cs_escalation">Escalação CS</SelectItem>
                    <SelectItem value="kommo_review">Revisão Kommo</SelectItem>
                    <SelectItem value="regular">Regular</SelectItem>
                  </SelectContent>
                </Select>

                {/* Contact Filter */}
                <div className="w-[240px]">
                  <ContactPicker
                    value={contactFilter}
                    onChange={setContactFilter}
                    placeholder="Cliente..."
                  />
                </div>



                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-9 rounded-full text-muted-foreground"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Limpar
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-hidden px-4 md:px-6 pb-4">
        <div className="flex gap-3 overflow-x-auto pb-4 h-[calc(100dvh-200px)] md:h-[calc(100dvh-200px)] max-md:h-[calc(100dvh-264px)]">
            {columns.map((column) => {
              const config = statusConfig[column.id] || statusConfig.pending;
              return (
              <div
                key={column.id}
                className="flex flex-col flex-shrink-0 w-[280px] min-w-[260px] max-w-[320px] h-full"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                <div className={`${config.bgColor} ${config.borderColor} border rounded-t-lg px-4 py-3 flex-shrink-0`}>
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 truncate">{t(column.labelKey)}</h3>
                    <span className={`${config.countBg} ${config.countText} px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0`}>
                      {contactFilter ? getTasksByStatus(column.id).length : (counts[column.id] || getTasksByStatus(column.id).length)}
                    </span>
                  </div>
                </div>

                <div className={`${config.bgColor} ${config.borderColor} border-x border-b rounded-b-lg flex-1 overflow-y-auto scrollbar-hide space-y-3 p-3`}>
                  {getTasksByStatus(column.id).map((task) => (
                    <Card
                      key={task.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task.id)}
                      onClick={() => handleOpenDetail(task)}
                      className={cn(
                        "cursor-pointer transition-all duration-200",
                        "hover:shadow-lg hover:border-primary/50",
                        "active:scale-[0.98]"
                      )}
                    >
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-medium leading-tight">{task.title}</h4>
                            <Badge variant={getPriorityColor(task.priority)} className="text-xs">
                              {getPriorityLabel(task.priority)}
                            </Badge>
                          </div>

                          {task.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {task.description}
                            </p>
                          )}

                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            {task.due_date && (
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {parseDateSafe(task.due_date)?.toLocaleDateString('pt-BR') || ''}
                              </div>
                            )}

                            {task.assigned_to && (
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {task.assigned_user_name || users?.find(u => u.id === task.assigned_to)?.display_name || t('tasks.assigned')}
                              </div>
                            )}

                            {task.estimated_hours && (
                              <div className="flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {task.estimated_hours}h
                              </div>
                            )}
                          </div>

                          {/* Escalation Badge */}
                          {task.metadata?.source === 'cs_escalation' && (
                            <div className="flex flex-col gap-1.5 pt-2 border-t border-border">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-800 text-xs">
                                  🔺 Escalação CS
                                </Badge>
                                {task.priority === 'urgent' && (
                                  <Badge variant="destructive" className="text-xs">
                                    Urgente
                                  </Badge>
                                )}
                                {task.priority === 'high' && (
                                  <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                    Alta
                                  </Badge>
                                )}
                              </div>
                              {task.metadata?.session_id && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <User className="w-3 h-3" />
                                  <span className="font-medium text-foreground">
                                    {escalationContacts[task.metadata.session_id]?.contactName || 'Cliente não identificado'}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Kommo Review Action Buttons */}
                          {task.metadata?.type === 'kommo_review' && task.status === 'pending' && (
                            <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="flex-1 text-emerald-600 border-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleKommoReviewAction(task, 'belongs');
                                }}
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Pertence
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="flex-1 text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleKommoReviewAction(task, 'not_belongs');
                                }}
                              >
                                <X className="w-3 h-3 mr-1" />
                                Não Pertence
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {getTasksByStatus(column.id).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      {t('tasks.noTasksInColumn')}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
        </div>
      </div>
      
      {/* Task Detail Sheet */}
      <TaskDetailSheet
        task={selectedTask}
        open={isDetailOpen}
        onOpenChange={(open) => {
          setIsDetailOpen(open);
          if (!open) setSelectedTask(null);
        }}
        users={users}
        onUpdate={handleUpdateTask}
        onDelete={handleDeleteTask}
        onComplete={handleCompleteFromSheet}
        isUpdating={updateTask.isPending}
        isDeleting={deleteTask.isPending}
      />
      
      {/* Escalation Task Completion Modal */}
      <EscalationTaskCompletionModal
        open={showEscalationModal}
        onClose={() => {
          setShowEscalationModal(false);
          setEscalationTask(null);
        }}
        task={escalationTask}
        contactName={escalationTask?.metadata?.session_id ? escalationContacts[escalationTask.metadata.session_id]?.contactName : null}
        onComplete={handleEscalationComplete}
        isSubmitting={updateTask.isPending}
      />
    </div>
  );
}
